<template>
  <article
    class="net-area"
    :style="{
      transform: `translate3d(${ctx.area.x}px, ${ctx.area.y}px, 0)`,
      width: `${ctx.area.width}px`,
      height: `${ctx.area.height}px`
    }"
    :class="{
      'is-highlighted': isHighlighted,
      'is-transparent': isTransparent
    }"
  >
    <div
      class="net-area__content"
      :class="{
        '-left': isLeft,
        '-right': !isLeft,
        '-top': isTop,
        '-bottom': !isTop
      }"
    >
      <h5 class="net-area__title" @pointerdown="select">
        {{ ctx.area.title }}
      </h5>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed } from "vue";

import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useArea } from "@editor/items/area/areas.store";

import type { Handleable } from "@core/types/Events";
import type { AreaItem } from "./AreaItem";

interface Props {
  id: number;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  (e: "select", area: AreaItem): void;
}>();

const ctx = useArea(() => props.id);

// ================================
// STATE

const isHighlighted = computed(() => {
  const highlighted = useSelectionStore().highlighted;
  const highlightedAreas = highlighted.area || [];
  return highlightedAreas.includes(ctx.area.id);
});

const isLeft = computed(() => {
  return ctx.area.x < 1000 / 2;
});

const isTop = computed(() => {
  return ctx.area.y < 700 / 2;
});

const isTransparent = computed(() => {
  return false;
});

// ================================
// METHODS

function select(mouseEvent: MouseEvent & Handleable) {
  mouseEvent.hasBeenHandled = true;
  emit("select", ctx.area);
}
</script>

<style lang="scss">
.net-area {
  position: absolute;
  pointer-events: none;
  background-color: rgba(255, 255, 255, 0.3);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.07);

  &.is-transparent {
    box-shadow: none;
  }

  .is-highlighted & {
    border-color: var(--color-action);
    border-width: 1px;
    border-style: solid;
  }

  &__content {
    position: absolute;
    width: 152px;
    pointer-events: auto;

    &.-left {
      left: 20px;
    }
    &.-right {
      right: 20px;
      text-align: right;
    }
    &.-top {
      top: 13px;
    }
    &.-bottom {
      bottom: 13px;
    }
  }

  &__title {
    font-size: 1em;
    font-weight: 400;
    min-height: 22px;
    margin: 0;
    margin-bottom: 0.875em;
  }
}
</style>
